'use client';

import type { UnstyledButtonProps } from '../unstyled-button';
import type { Amendment } from '@paid-ui/types/amendment';
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

import { createContext, forwardRef, useContext, useId, useState } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { isNil } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AmendType } from '@paid-ui/enums/amendment';
import { getFieldValueFromSnapshot } from '@paid-ui/forms/contract/prime/utils/get-snapshot';
import { ChevronDownIcon } from '@paid-ui/icons/chevron-down';
import { RevertIcon } from '@paid-ui/icons/revert';

import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '../popover';
import { UnstyledButton } from '../unstyled-button';

const amendmentVariants = cva('', {
  variants: {
    fromYou: {
      true: 'You',
      false: 'They',
    },
    type: {
      ADD: 'added',
      REMOVE: 'removed',
      UPDATE: 'updated',
      NONE: '',
    },
  },
});

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

interface FormItemContextValue {
  id: string;
  // If snapshot popover is open or not
  open: boolean;
  // Set snapshot popover open function
  setOpen: (value: boolean) => void;
}

type FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ControllerProps<TFieldValues, TName>;

export const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);
export const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FormFieldProps<TFieldValues, TName> & { hidden?: boolean },
) => {
  const { name, hidden, ...restProps } = props;

  if (hidden) {
    return null;
  }

  return (
    <FormFieldContext.Provider value={{ name }}>
      <Controller name={name} {...restProps} />
    </FormFieldContext.Provider>
  );
};

export const FormItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, hidden, ...restProps }, ref) => {
    const id = useId();
    const [open, setOpen] = useState(false);

    if (hidden) {
      return null;
    }

    return (
      <FormItemContext.Provider value={{ id, open, setOpen }}>
        <div ref={ref} className={twMerge('w-full space-y-2', className)} {...restProps} />
      </FormItemContext.Provider>
    );
  },
);

export const useFormField = () => {
  const fieldContext = useContext(FormFieldContext);
  const itemContext = useContext(FormItemContext);
  const { watch, trigger, getValues, getFieldState, setValue, setError, setFocus, formState } =
    useFormContext();
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { name } = fieldContext;
  const { id, open, setOpen } = itemContext;

  return {
    id,
    name,
    open,

    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,

    ...fieldState,

    watch,
    trigger,
    getValue: () => getValues(name),
    getValues,

    getDefaultValue: () => getFieldValueFromSnapshot(formState.defaultValues, name),
    getDefaultValues: () => formState.defaultValues,

    setOpen,
    setValue: (
      value: any,
      options?: Partial<{
        shouldValidate: boolean;
        shouldDirty: boolean;
        shouldTouch: boolean;
      }>,
    ) => setValue(name, value, options),
    setError: (
      error: any,
      options?: {
        shouldFocus: boolean;
      },
    ) => setError(name, error, options),
    setFocus: (
      options?: Partial<{
        shouldSelect: boolean;
      }>,
    ) => setFocus(name, options),
  };
};

export const Asterisk = forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ hidden, children = '∗', className, ...restProps }, ref) => {
    if (hidden) {
      return null;
    }

    return (
      <span ref={ref} className={twMerge('align-top text-sm text-pink', className)} {...restProps}>
        {children}
      </span>
    );
  },
);

export const FormLabel = forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ hidden, className, children, ...restProps }, ref) => {
  const { formItemId } = useFormField();

  if (hidden || !children) {
    return null;
  }

  return (
    <LabelPrimitive.Root
      ref={ref}
      htmlFor={formItemId}
      className={twMerge('text-base font-medium', className)}
      {...restProps}
    >
      {children}
    </LabelPrimitive.Root>
  );
});

export const FormDescription = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ hidden, className, children, ...restProps }, ref) => {
    const { formDescriptionId } = useFormField();

    if (!children) {
      return null;
    }

    return (
      <div
        ref={ref}
        id={formDescriptionId}
        className={twMerge('text-sm text-grey-dark', className)}
        {...restProps}
      >
        {children}
      </div>
    );
  },
);

export const FormMessage = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ hidden, className, children, ...restProps }, ref) => {
    const { error, formMessageId } = useFormField();
    const errorMessage = error
      ? String(error.message).replace('[CRITICAL] ', '')
      : typeof children === 'string'
        ? children.replace('[CRITICAL] ', '')
        : children;

    if (hidden || !errorMessage) {
      return null;
    }

    return (
      <div
        ref={ref}
        id={formMessageId}
        className={twMerge('whitespace-normal text-sm font-medium text-pink', className)}
        {...restProps}
      >
        {errorMessage}
      </div>
    );
  },
);

export const FormGroup = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ hidden, className, children, ...restProps }, ref) => {
    if (hidden || !children) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={twMerge('grid grid-cols-1 gap-y-9 sm:grid-cols-2 sm:gap-x-3.5', className)}
        {...restProps}
      >
        {children}
      </div>
    );
  },
);

export const RevertButton = forwardRef<HTMLButtonElement, UnstyledButtonProps>(
  ({ hidden, className, children, ...restProps }, ref) => {
    if (hidden) {
      return null;
    }

    return (
      <UnstyledButton
        ref={ref}
        className={twMerge(
          'inline-flex shrink-0 cursor-pointer items-center gap-x-2 text-blue transition-colors duration-150 ease-linear hover:text-blue-dark',
          className,
        )}
        {...restProps}
      >
        <RevertIcon className="h-[18px] w-[18px]" />
        {children}
      </UnstyledButton>
    );
  },
);

export const FormContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { error?: boolean }
>((props, ref) => {
  const { error, ...restProps } = props;
  return <div ref={ref} {...restProps} />;
});

FormContainer.displayName = 'FormContainer';

export const FormControl = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Slot> & {
    error?: boolean;
  }
>((props, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      error={Boolean(error)}
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : `${formDescriptionId}`}
      aria-invalid={Boolean(error)}
      {...props}
    />
  );
});

export const FormHelper = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => {
    const { hidden, children, className, ...restProps } = props;

    if (hidden) {
      return null;
    }

    if (!children) {
      return (
        <div
          ref={ref}
          className={twMerge('flex w-full gap-x-4 empty:hidden', className)}
          {...restProps}
        >
          <FormMessage />
        </div>
      );
    }

    return (
      <div ref={ref} className={twMerge('space-y-1 empty:hidden', className)} {...restProps}>
        <FormDescription>{children}</FormDescription>
        <FormMessage />
      </div>
    );
  },
);

interface FormChangedProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  changed?: 'you' | 'they' | 'both';
}

export const FormChanged = forwardRef<HTMLButtonElement, FormChangedProps>(
  ({ changed, hidden, className, children, ...restProps }, ref) => {
    const { error, setOpen } = useFormField();

    if (hidden || !changed) {
      return null;
    }

    const changedText = {
      you: 'You changed',
      they: 'They changed',
      both: 'You both changed',
    } as const;

    return (
      <UnstyledButton
        ref={ref}
        onClick={() => setOpen(true)}
        className={twMerge(
          'inline-flex h-[21px] shrink-0 cursor-pointer items-center gap-x-1.5 self-start text-purple transition-colors duration-150 ease-linear hover:text-purple-light active:text-purple-dark',
          className,
        )}
        {...restProps}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="5"
          height="10"
          viewBox="0 0 5 10"
          fill="currentColor"
          className="shrink-0"
        >
          <path d="M0 10L5 5L-4.37114e-07 0L0 10Z" />
        </svg>
        <span className="text-sm font-medium text-current">{changedText[changed]}</span>
      </UnstyledButton>
    );
  },
);

interface FormChanged2Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  index: number;
  amendment?: Amendment | null;
  disableRestore?: boolean;
  onUndo?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onRestore?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

// Used for list items (e.g. progress payments fields)
export const FormChanged2 = forwardRef<HTMLButtonElement, FormChanged2Props>(
  (
    { index, amendment = null, disableRestore, onUndo, onRestore, hidden, className, ...restProps },
    ref,
  ) => {
    if (!amendment || amendment.type === AmendType.NONE || hidden) {
      return null;
    }

    const youRemoved = amendment.type === AmendType.REMOVE && amendment.fromYou;
    const removed = disableRestore ? youRemoved : amendment.type === AmendType.REMOVE;

    const amendmentText = amendmentVariants({
      fromYou: amendment.fromYou,
      type: amendment.type,
    });

    const lastAmendmentText = amendment._lastFromYou
      ? ''
      : amendmentVariants({
          fromYou: amendment._lastFromYou,
          type: amendment._lastAmendType,
        });

    return (
      <Popover>
        <PopoverTrigger asChild>
          <UnstyledButton
            ref={ref}
            className={twMerge(
              'inline-flex h-[21px] shrink-0 items-center gap-x-2 rounded-md text-purple',
              removed ? 'cursor-pointer' : 'pointer-events-none',
              className,
            )}
            {...restProps}
          >
            <span className="h-2 w-2 shrink-0 rounded bg-current" />
            <span className="inline-flex items-center gap-x-1">
              <span className="text-sm font-medium text-current">
                {[lastAmendmentText, amendmentText].filter(Boolean).join(', ')}
              </span>
              {removed ? <ChevronDownIcon className="h-4 w-4 shrink-0" /> : null}
            </span>
          </UnstyledButton>
        </PopoverTrigger>
        <PopoverContent sideOffset={-20} className="flex items-center justify-between gap-x-2 p-4">
          <div className="inline-flex items-center gap-x-2 text-purple">
            <span className="h-2 w-2 shrink-0 rounded bg-current" />
            <span className="inline-flex items-center gap-x-1">
              <span className="text-sm font-medium text-current">{amendmentText}</span>
            </span>
          </div>
          <PopoverClose asChild>
            {amendment.fromYou ? (
              <RevertButton data-index={index} onClick={onUndo}>
                Undo
              </RevertButton>
            ) : (
              <RevertButton data-index={index} onClick={onRestore}>
                Restore
              </RevertButton>
            )}
          </PopoverClose>
        </PopoverContent>
      </Popover>
    );
  },
);

export const SnapshotValue = forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & { value?: any }
>(({ value, hidden, className, ...restProps }, ref) => {
  if (hidden) {
    return null;
  }

  if (isNil(value) || value === '') {
    return (
      <p
        ref={ref}
        className={twMerge('select-none pl-[18px] text-grey-tertiary', className)}
        {...restProps}
      >
        No value
      </p>
    );
  }

  return (
    <p ref={ref} className={twMerge('pl-[18px]', className)} {...restProps}>
      {value}
    </p>
  );
});

FormField.displayName = 'FormField';
FormItem.displayName = 'FormItem';
FormLabel.displayName = 'FormLabel';
FormDescription.displayName = 'FormDescription';
FormMessage.displayName = 'FormMessage';
FormControl.displayName = 'FormControl';
FormHelper.displayName = 'FormHelper';
FormChanged.displayName = 'FormChanged';
FormChanged2.displayName = 'FormChanged2';
FormGroup.displayName = 'FormGroup';

Asterisk.displayName = 'Asterisk';
RevertButton.displayName = 'RevertButton';
SnapshotValue.displayName = 'SnapshotValue';

export { FormProvider as Form } from 'react-hook-form';
